import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { vertraegeApi, dokumenteApi, preishistorieApi, optimierungApi, radarApi, actionsApi, financialImpactApi, notificationsApi, bankStatementsApi } from '../api';
import KiAnalyse from '../components/KiAnalyse';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

function formatDE(datum) {
  if (!datum) return '–';
  const [y, m, d] = String(datum).split('-');
  return `${d}.${m}.${y}`;
}

const DRINGLICHKEIT_FARBE = {
  kritisch: '#ef4444', hoch: '#f97316', mittel: '#eab308',
  niedrig: '#22c55e', vergangen: '#64748b',
};

const STATUS_LABELS = {
  aktiv: 'Aktiv', laeuft_bald_aus: 'Läuft bald aus', kuendigungsfrist_laeuft: 'Kündigungsfrist läuft',
  frist_verpasst: 'Frist verpasst', gekuendigt: 'Gekündigt', abgelaufen: 'Abgelaufen',
  verlaengert: 'Verlängert', archiviert: 'Archiviert',
};

const TYP_ICONS = {
  letzte_kuendigung: '✂️', vertragsende: '🏁', naechste_verlaengerung: '🔄',
  erinnerung_90: '🔔', erinnerung_30: '🔔', erinnerung_7: '⚠️',
  vertragsbeginn: '🚀', vertrag_angelegt: '📋',
};

// ─── Timeline-Komponente ──────────────────────────────────────────────────────

function TimelineTab({ id }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    vertraegeApi.timeline(id)
      .then(r => setData(r.data))
      .catch(() => setError('Timeline konnte nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div style={{ color: '#6366f1', padding: 32, textAlign: 'center' }}>Timeline wird geladen…</div>;
  if (error) return <div style={{ color: '#ef4444', padding: 16 }}>{error}</div>;
  if (!data || !data.ereignisse?.length) return (
    <div style={{ color: '#64748b', padding: 32, textAlign: 'center' }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>📅</div>
      <div>Keine Timeline-Daten vorhanden.</div>
      <div style={{ fontSize: 12, marginTop: 8 }}>
        Bitte hinterlege Vertragsbeginn, Laufzeit und Kündigungsfrist.
      </div>
    </div>
  );

  const naechstes = data.naechstesEreignis;

  return (
    <div>
      {naechstes && (
        <div style={{ background: '#1e3a5f', borderRadius: 10, padding: 16, marginBottom: 20,
          border: `2px solid ${DRINGLICHKEIT_FARBE[naechstes.dringlichkeit] || '#6366f1'}` }}>
          <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', marginBottom: 4 }}>
            Nächster Termin
          </div>
          <div style={{ color: '#f1f5f9', fontWeight: 700, fontSize: 16 }}>
            {TYP_ICONS[naechstes.typ] || '📅'} {naechstes.label}
          </div>
          <div style={{ color: DRINGLICHKEIT_FARBE[naechstes.dringlichkeit] || '#6366f1', fontSize: 14, marginTop: 4 }}>
            {naechstes.datum_de} · noch {naechstes.tage_bis_ereignis} Tage
          </div>
        </div>
      )}

      <div style={{ position: 'relative', paddingLeft: 24 }}>
        <div style={{ position: 'absolute', left: 8, top: 0, bottom: 0, width: 2, background: '#334155' }} aria-hidden="true" />
        {data.ereignisse.map((e, i) => {
          const farbe = DRINGLICHKEIT_FARBE[e.dringlichkeit] || '#64748b';
          const isFuture = !e.vergangen;
          return (
            <div key={i} style={{ position: 'relative', marginBottom: 16, opacity: e.vergangen ? 0.6 : 1 }}>
              <div style={{ position: 'absolute', left: -20, top: 6, width: 12, height: 12,
                borderRadius: '50%', background: farbe, border: '2px solid #0f172a' }} aria-hidden="true" />
              <div style={{ background: '#1e293b', borderRadius: 10, padding: 12,
                borderLeft: `3px solid ${farbe}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                  <div>
                    <span style={{ marginRight: 6 }} aria-hidden="true">{TYP_ICONS[e.typ] || '📅'}</span>
                    <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14 }}>{e.label}</span>
                    {e.vergangen && <span style={{ marginLeft: 6, color: '#64748b', fontSize: 11 }}>(vergangen)</span>}
                  </div>
                  <span style={{ color: farbe, fontWeight: 700, fontSize: 13 }}>{e.datum_de}</span>
                </div>
                <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>{e.beschreibung}</div>
                {isFuture && e.tage_von_heute !== undefined && (
                  <div style={{ color: farbe, fontSize: 11, marginTop: 4 }}>
                    Noch {e.tage_von_heute} Tage
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Simulator-Komponente ─────────────────────────────────────────────────────

function SimulatorTab({ id }) {
  const heute = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ simulationsdatum: heute, verlaengerungszyklen: 3 });
  const [ergebnis, setErgebnis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const simuliere = async () => {
    setLoading(true); setError(null); setErgebnis(null);
    try {
      const res = await vertraegeApi.simulieren(id, form);
      setErgebnis(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Simulation fehlgeschlagen.');
    } finally { setLoading(false); }
  };

  const schnellwahl = (tage) => {
    const d = new Date();
    d.setDate(d.getDate() + tage);
    setForm(f => ({ ...f, simulationsdatum: d.toISOString().slice(0, 10) }));
  };

  const inp = { background: '#0f172a', color: '#f1f5f9', border: '1px solid #334155',
    borderRadius: 8, padding: '8px 12px', fontSize: 14, width: '100%', boxSizing: 'border-box' };

  return (
    <div>
      <h3 style={{ color: '#f1f5f9', fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
        🔮 Fristen simulieren
      </h3>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>
        Simuliere Auswirkungen eines Kündigungstermins. Die Simulation verändert keine gespeicherten Daten.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {[0, 7, 30].map(t => (
          <button key={t} onClick={() => schnellwahl(t)}
            style={{ background: '#334155', color: '#94a3b8', border: 'none', borderRadius: 6,
              padding: '6px 12px', cursor: 'pointer', fontSize: 12 }}>
            {t === 0 ? 'Heute' : `In ${t} Tagen`}
          </button>
        ))}
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 4 }}>
          Simuliertes Kündigungsdatum
        </label>
        <input type="date" value={form.simulationsdatum}
          onChange={e => setForm(f => ({ ...f, simulationsdatum: e.target.value }))} style={inp} />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 4 }}>
          Verlängerungszyklen (max. 10)
        </label>
        <input type="number" min={0} max={10} value={form.verlaengerungszyklen}
          onChange={e => setForm(f => ({ ...f, verlaengerungszyklen: parseInt(e.target.value) || 1 }))} style={inp} />
      </div>

      <button onClick={simuliere} disabled={loading}
        style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8,
          padding: '10px 20px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600,
          opacity: loading ? 0.7 : 1 }}>
        {loading ? 'Wird berechnet…' : 'Fristen simulieren'}
      </button>

      {error && (
        <div style={{ background: '#431407', color: '#fca5a5', border: '1px solid #ef4444', borderRadius: 8, padding: 12, marginTop: 16 }}>
          {error}
        </div>
      )}

      {ergebnis && (
        <div style={{ marginTop: 20 }}>
          <div style={{ background: ergebnis.rechtzeitig ? '#052e16' : '#431407',
            color: ergebnis.rechtzeitig ? '#86efac' : '#fca5a5',
            border: `1px solid ${ergebnis.rechtzeitig ? '#22c55e' : '#ef4444'}`,
            borderRadius: 10, padding: 16, marginBottom: 12, fontWeight: 700, fontSize: 15 }}>
            {ergebnis.rechtzeitig ? '✅ Kündigung noch rechtzeitig möglich' : '❌ Kündigung zu spät'}
          </div>

          <div style={{ background: '#1e293b', borderRadius: 10, padding: 16, marginBottom: 12 }}>
            <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>Simulation vom {formatDE(ergebnis.simulationsdatum)}</div>
            <div style={{ color: '#f1f5f9', fontSize: 14, lineHeight: 1.7 }}>
              {[
                ['Letzter Kündigungstermin', formatDE(ergebnis.letzte_kuendigung_moeglich)],
                ['Aktuelles Vertragsende', formatDE(ergebnis.aktuelles_vertragsende)],
                ['Verbleibende Tage', ergebnis.verbleibeneTage !== null ? `${ergebnis.verbleibeneTage} Tage` : '–'],
                ['Resultierender Status', STATUS_LABELS[ergebnis.resultierender_status] || ergebnis.resultierender_status],
              ].map(([label, val]) => (
                <div key={label} style={{ display: 'flex', gap: 8, borderBottom: '1px solid #334155', padding: '4px 0' }}>
                  <span style={{ color: '#64748b', minWidth: 200 }}>{label}:</span>
                  <span style={{ fontWeight: 600 }}>{val}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: '#1e293b', borderRadius: 10, padding: 16, marginBottom: 12 }}>
            <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>Zusammenfassung</div>
            <div style={{ color: '#f1f5f9', fontSize: 14 }}>{ergebnis.zusammenfassung}</div>
          </div>

          {ergebnis.warnungen?.length > 0 && (
            <div style={{ background: '#422006', border: '1px solid #f59e0b', borderRadius: 10, padding: 12, marginBottom: 8 }}>
              {ergebnis.warnungen.map((w, i) => <div key={i} style={{ color: '#fde047', fontSize: 13 }}>⚠️ {w}</div>)}
            </div>
          )}
          {ergebnis.empfehlung?.length > 0 && (
            <div style={{ background: '#1e1b4b', border: '1px solid #818cf8', borderRadius: 10, padding: 12, marginBottom: 8 }}>
              {ergebnis.empfehlung.map((e, i) => <div key={i} style={{ color: '#c7d2fe', fontSize: 13 }}>💡 {e}</div>)}
            </div>
          )}

          {ergebnis.verlaengerungsprognose?.length > 0 && (
            <div style={{ background: '#1e293b', borderRadius: 10, padding: 16 }}>
              <div style={{ color: '#818cf8', fontWeight: 700, marginBottom: 8 }}>Verlängerungsprognose</div>
              {ergebnis.verlaengerungsprognose.map(z => (
                <div key={z.zyklus} style={{ borderBottom: '1px solid #334155', padding: '6px 0', fontSize: 13 }}>
                  <span style={{ color: '#6366f1', fontWeight: 700, marginRight: 8 }}>Zyklus {z.zyklus}:</span>
                  <span style={{ color: '#f1f5f9' }}>Vertragsende {formatDE(z.vertragsende)}</span>
                  {z.letzte_kuendigung_moeglich && (
                    <span style={{ color: '#94a3b8', marginLeft: 8 }}>· Kündigung bis {formatDE(z.letzte_kuendigung_moeglich)}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Prognose-Komponente ──────────────────────────────────────────────────────

function PrognoseTab({ id }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    vertraegeApi.prognose(id, 5)
      .then(r => setData(r.data))
      .catch(() => setError('Prognose konnte nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div style={{ color: '#6366f1', padding: 32, textAlign: 'center' }}>Prognose wird berechnet…</div>;
  if (error) return <div style={{ color: '#ef4444', padding: 16 }}>{error}</div>;
  if (!data?.auto_renewal || !data?.prognose?.length) return (
    <div style={{ color: '#64748b', padding: 32, textAlign: 'center' }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>🔄</div>
      <div>Keine automatische Verlängerung aktiv oder kein Verlängerungszeitraum konfiguriert.</div>
      {data?.hinweis && <div style={{ fontSize: 12, marginTop: 8 }}>{data.hinweis}</div>}
    </div>
  );

  return (
    <div>
      <h3 style={{ color: '#f1f5f9', fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
        🔄 Verlängerungsprognose
      </h3>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>
        Verlängerungszeitraum: {data.verlaengerungszeitraum_monate} Monate
      </p>
      {data.prognose.map(z => (
        <div key={z.zyklus} style={{ background: '#1e293b', borderRadius: 10, padding: 14, marginBottom: 8,
          borderLeft: '3px solid #6366f1' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#818cf8', fontWeight: 700 }}>Zyklus {z.zyklus}</span>
            <span style={{ color: '#f1f5f9', fontWeight: 700 }}>Ende: {formatDE(z.vertragsende)}</span>
          </div>
          <div style={{ color: '#94a3b8', fontSize: 13 }}>
            {z.letzte_kuendigung_moeglich && <div>Kündigung bis: <strong style={{ color: '#f1f5f9' }}>{formatDE(z.letzte_kuendigung_moeglich)}</strong></div>}
            {z.erinnerung_1 && <div>1. Erinnerung: {formatDE(z.erinnerung_1)}</div>}
            {z.erinnerung_2 && <div>2. Erinnerung: {formatDE(z.erinnerung_2)}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Vertragshistorie-Komponente ──────────────────────────────────────────────

function HistorieTab({ id }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    vertraegeApi.historie(id)
      .then(r => setData(r.data))
      .catch(() => setError('Historie konnte nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div style={{ color: '#6366f1', padding: 32, textAlign: 'center' }}>Historie wird geladen…</div>;
  if (error) return <div style={{ color: '#ef4444', padding: 16 }}>{error}</div>;
  if (!data?.eintraege?.length) return (
    <div style={{ color: '#64748b', padding: 32, textAlign: 'center' }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>📜</div>
      <div>Noch keine Historieneinträge vorhanden.</div>
      <div style={{ fontSize: 12, marginTop: 8 }}>Änderungen werden ab jetzt protokolliert.</div>
    </div>
  );

  const TYP_ICONS_H = {
    angelegt: '📋', bearbeitet: '✏️', status_geaendert: '🔄', vertragsende_geaendert: '📅',
    gekuendigt: '✂️', archiviert: '🗄️', dokument_hinzugefuegt: '📄', fristen_berechnet: '⚙️',
  };

  return (
    <div>
      {data.eintraege.map(e => (
        <div key={e.id} style={{ background: '#1e293b', borderRadius: 10, padding: 12, marginBottom: 8,
          display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 18, lineHeight: 1.2 }} aria-hidden="true">
            {TYP_ICONS_H[e.ereignistyp] || '📝'}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
              <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14 }}>{e.titel}</span>
              <span style={{ color: '#64748b', fontSize: 11 }}>{e.created_at_de}</span>
            </div>
            {e.beschreibung && <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>{e.beschreibung}</div>}
            {e.neue_werte && Object.keys(e.neue_werte).length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', marginTop: 6 }}>
                {Object.entries(e.neue_werte).map(([key, val]) => (
                  <span key={key} style={{ fontSize: 11, background: '#0f172a', borderRadius: 4, padding: '2px 8px', border: '1px solid #334155' }}>
                    <span style={{ color: '#64748b' }}>{key}: </span>
                    <span style={{ color: '#f1f5f9' }}>{String(val)}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Dokumentenanalyse-Tab ────────────────────────────────────────────────────

const RISIKO_FARBE = { gering: '#22c55e', mittel: '#eab308', hoch: '#f97316', kritisch: '#ef4444' };
const STATUS_VERARB = {
  ausstehend: { label: 'Ausstehend', color: '#64748b' },
  wird_verarbeitet: { label: 'In Verarbeitung', color: '#6366f1' },
  abgeschlossen: { label: 'Analysiert', color: '#22c55e' },
  fehlgeschlagen: { label: 'Fehler', color: '#ef4444' },
};

function DokumentAnalyseTab({ dokumente, onAnalyseStarten }) {
  const navigate = useNavigate();
  if (!dokumente.length) {
    return <EmptyState text="Noch keine Dokumente vorhanden. Lade ein Dokument im Tab 'Dokumente' hoch." />;
  }

  const mitRisiko = dokumente.filter(d => d.risiko_stufe && d.risiko_stufe !== 'keine');
  const risikoKritisch = mitRisiko.filter(d => d.risiko_stufe === 'kritisch' || d.risiko_stufe === 'hoch');

  return (
    <div>
      {risikoKritisch.length > 0 && (
        <div style={{ background: '#431407', border: '1px solid #ef4444', borderRadius: 10, padding: 14, marginBottom: 16 }}>
          <div style={{ color: '#f87171', fontWeight: 700, marginBottom: 6 }}>
            ⚠️ {risikoKritisch.length} Dokument{risikoKritisch.length > 1 ? 'e' : ''} mit kritischem/hohem Risiko
          </div>
          {risikoKritisch.map(d => (
            <div key={d.id} style={{ color: '#fca5a5', fontSize: 13 }}>
              • {d.originaldateiname || d.dateiname}
              {d.risiko_stufe && <span style={{ color: RISIKO_FARBE[d.risiko_stufe] || '#94a3b8', marginLeft: 6 }}>({d.risiko_stufe})</span>}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gap: 10 }}>
        {dokumente.map(doc => {
          const vstatus = STATUS_VERARB[doc.verarbeitungsstatus] || STATUS_VERARB.ausstehend;
          const isRunning = doc.verarbeitungsstatus === 'wird_verarbeitet';
          const isDone = doc.verarbeitungsstatus === 'abgeschlossen';
          const isFailed = doc.verarbeitungsstatus === 'fehlgeschlagen';

          return (
            <div key={doc.id} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 14 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                    <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14, overflowWrap: 'anywhere' }}>
                      {doc.originaldateiname || doc.dateiname}
                    </span>
                    <span style={{ background: vstatus.color + '22', color: vstatus.color, border: `1px solid ${vstatus.color}55`,
                      borderRadius: 6, padding: '2px 8px', fontSize: 11, whiteSpace: 'nowrap' }}>
                      {isRunning && '⚙️ '}{vstatus.label}
                    </span>
                    {doc.risiko_stufe && doc.risiko_stufe !== 'keine' && (
                      <span style={{ background: (RISIKO_FARBE[doc.risiko_stufe] || '#94a3b8') + '22',
                        color: RISIKO_FARBE[doc.risiko_stufe] || '#94a3b8',
                        border: `1px solid ${(RISIKO_FARBE[doc.risiko_stufe] || '#94a3b8')}55`,
                        borderRadius: 6, padding: '2px 8px', fontSize: 11 }}>
                        Risiko: {doc.risiko_stufe}
                      </span>
                    )}
                  </div>

                  {doc.klassifikation && (
                    <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>
                      Typ: <span style={{ color: '#cbd5e1' }}>{doc.klassifikation}</span>
                      {doc.klassifikation_konfidenz && (
                        <span style={{ color: '#64748b', marginLeft: 6 }}>
                          ({Math.round((doc.klassifikation_konfidenz || 0) * 100)}% Konfidenz)
                        </span>
                      )}
                    </div>
                  )}

                  {doc.zusammenfassung && (
                    <div style={{ color: '#94a3b8', fontSize: 12, fontStyle: 'italic', marginBottom: 4,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 420 }}>
                      {doc.zusammenfassung}
                    </div>
                  )}

                  {isRunning && (
                    <div style={{ marginTop: 6 }}>
                      <div style={{ background: '#334155', borderRadius: 4, height: 4 }}>
                        <div style={{ background: '#6366f1', height: 4, borderRadius: 4, width: `${doc.verarbeitungsfortschritt || 0}%`,
                          transition: 'width 0.5s', animation: 'pulse 1.5s infinite' }} />
                      </div>
                    </div>
                  )}

                  {isFailed && doc.fehlermeldung && (
                    <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>Fehler: {doc.fehlermeldung}</div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {!isDone && !isRunning && (
                    <button onClick={() => onAnalyseStarten(doc.id)}
                      style={{ background: '#6366f122', color: '#818cf8', border: '1px solid #6366f155',
                        borderRadius: 7, padding: '6px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                      {isFailed ? '↻ Erneut' : '▶ Analyse'}
                    </button>
                  )}
                  <button onClick={() => navigate(`/dokumente/${doc.id}`)}
                    style={{ background: '#1e3a5f', color: '#38bdf8', border: '1px solid #1d4ed855',
                      borderRadius: 7, padding: '6px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                    Details →
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── EPIC 8: Radar-Tab ───────────────────────────────────────────────────────

const RADAR_SEV_CFG = {
  critical: { label: 'Kritisch', color: '#ef4444', icon: '🔴' },
  high:     { label: 'Hoch',     color: '#f97316', icon: '🟠' },
  medium:   { label: 'Mittel',   color: '#eab308', icon: '🟡' },
  low:      { label: 'Niedrig',  color: '#22c55e', icon: '🟢' },
  info:     { label: 'Info',     color: '#64748b', icon: '⚪' },
};

const PRIO_CONFIG = {
  CRITICAL: { label: '🔥 Kritisch', color: '#ef4444', bg: '#3b0000' },
  HIGH: { label: '⚠️ Hoch', color: '#f59e0b', bg: '#1c1400' },
  NORMAL: { label: '● Normal', color: '#3b82f6', bg: '#0c1a2e' },
  LOW: { label: '○ Niedrig', color: '#64748b', bg: '#0f172a' },
};
// ─── EPIC 12: FinanzenTab ─────────────────────────────────────────────────────
function FinanzenTab({ vertragId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    financialImpactApi.baseline()
      .then(b => {
        const contract = b.breakdown.find(c => c.vertragId === Number(vertragId));
        setData({ baseline: b, contract });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [vertragId]);

  if (loading) return <div style={{ padding: 24, color: '#94a3b8' }}>⏳ Lade Finanzdaten…</div>;
  if (!data || !data.contract) return (
    <div style={{ padding: 24 }}>
      <div style={{ color: '#f59e0b', background: '#422006', border: '1px solid #78350f', borderRadius: 8, padding: 12, fontSize: 13 }}>
        Keine Kostendaten für diesen Vertrag hinterlegt oder Vertrag nicht aktiv.
      </div>
    </div>
  );

  const { contract, baseline } = data;
  const fmt = v => (v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const share = baseline.monthlyCost > 0 ? ((contract.monthlyCost / baseline.monthlyCost) * 100).toFixed(1) : '0';

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 16 }}>
        {[
          { label: 'Monatliche Kosten', value: fmt(contract.monthlyCost) + ' €', color: '#818cf8' },
          { label: 'Jährliche Kosten', value: fmt(contract.monthlyCost * 12) + ' €', color: '#22d3ee' },
          { label: 'Anteil am Portfolio', value: share + ' %', color: '#f59e0b' },
        ].map(kpi => (
          <div key={kpi.label} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 14 }}>
            <div style={{ color: kpi.color, fontSize: 18, fontWeight: 700 }}>{kpi.value}</div>
            <div style={{ color: '#64748b', fontSize: 11, marginTop: 4 }}>{kpi.label}</div>
          </div>
        ))}
      </div>
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: 14, fontSize: 13 }}>
        <div style={{ color: '#94a3b8', marginBottom: 8 }}>Hochrechnungen (SCHÄTZUNG):</div>
        <div style={{ display: 'flex', gap: 10 }}>
          {[12, 24, 36].map(m => (
            <div key={m} style={{ flex: 1, background: '#0f172a', borderRadius: 8, padding: 10, textAlign: 'center' }}>
              <div style={{ color: '#22c55e', fontWeight: 700, fontSize: 15 }}>{fmt(contract.monthlyCost * m)} €</div>
              <div style={{ color: '#64748b', fontSize: 11 }}>{m} Monate</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ marginTop: 12, color: '#475569', fontSize: 11 }}>
        Provenienz: FAKT (Basisdaten) / SCHÄTZUNG (Hochrechnungen) · Kein Vertrag wird verändert.
      </div>
    </div>
  );
}

const STATUS_CONFIG_A = {
  OPEN: { label: 'Offen', color: '#22c55e' },
  COMPLETED: { label: 'Erledigt', color: '#64748b' },
  SNOOZED: { label: 'Verschoben', color: '#f59e0b' },
  DISMISSED: { label: 'Verworfen', color: '#475569' },
};

function AufgabenTab({ vertragId, navigate }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newPrio, setNewPrio] = useState('NORMAL');
  const [filter, setFilter] = useState('OPEN');

  const load = () => {
    setLoading(true);
    actionsApi.list({ vertragId, limit: 100 })
      .then(data => setActions(Array.isArray(data) ? data : (data.actions || [])))
      .catch(err => setError(err.response?.data?.error || 'Fehler beim Laden.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [vertragId]);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    try {
      await actionsApi.create({ title: newTitle.trim(), priority: newPrio, vertrag_id: Number(vertragId), source: 'MANUAL', auto_generated: 0 });
      setNewTitle(''); setShowForm(false); load();
    } catch (e) { alert('Fehler: ' + (e.response?.data?.error || e.message)); }
  };

  const handleComplete = async (id) => { try { await actionsApi.complete(id); load(); } catch (e) { alert('Fehler.'); } };
  const handleReopen = async (id) => { try { await actionsApi.reopen(id); load(); } catch (e) { alert('Fehler.'); } };
  const handleDismiss = async (id) => { if (!window.confirm('Aufgabe verwerfen?')) return; try { await actionsApi.dismiss(id); load(); } catch (e) { alert('Fehler.'); } };

  if (loading) return <div style={{ padding: 20, color: '#64748b' }}>⏳ Lade Aufgaben...</div>;
  if (error) return <div style={{ padding: 20, color: '#f87171' }}>{error}</div>;

  const filtered = filter === 'ALL' ? actions : actions.filter(a => a.status === filter);

  return (
    <div style={{ padding: '0 0 20px' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {['OPEN', 'COMPLETED', 'SNOOZED', 'ALL'].map(s => (
            <button key={s} onClick={() => setFilter(s)} style={{
              padding: '4px 10px', borderRadius: 6, border: '1px solid',
              borderColor: filter === s ? '#4f46e5' : '#334155',
              background: filter === s ? '#4f46e5' : 'transparent',
              color: filter === s ? '#fff' : '#94a3b8', fontSize: 12, cursor: 'pointer',
            }}>
              {s === 'ALL' ? 'Alle' : (STATUS_CONFIG_A[s]?.label || s)}
            </button>
          ))}
        </div>
        <button onClick={() => setShowForm(!showForm)} style={{
          marginLeft: 'auto', background: '#4f46e5', color: '#fff', border: 'none',
          borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
        }}>+ Aufgabe</button>
      </div>

      {showForm && (
        <div style={{ background: '#1e293b', borderRadius: 10, padding: 16, marginBottom: 16, border: '1px solid #334155' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', marginBottom: 10 }}>Neue Aufgabe</div>
          <input
            value={newTitle} onChange={e => setNewTitle(e.target.value)}
            placeholder="Titel der Aufgabe..."
            style={{ width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, padding: '8px 10px', color: '#f1f5f9', fontSize: 13, boxSizing: 'border-box', marginBottom: 10 }}
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select value={newPrio} onChange={e => setNewPrio(e.target.value)} style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, padding: '6px 10px', color: '#f1f5f9', fontSize: 12 }}>
              <option value="CRITICAL">🔥 Kritisch</option>
              <option value="HIGH">⚠️ Hoch</option>
              <option value="NORMAL">● Normal</option>
              <option value="LOW">○ Niedrig</option>
            </select>
            <button onClick={handleCreate} style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>Erstellen</button>
            <button onClick={() => setShowForm(false)} style={{ background: 'transparent', color: '#64748b', border: '1px solid #334155', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', fontSize: 13 }}>Abbrechen</button>
          </div>
        </div>
      )}

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: 32, color: '#64748b' }}>
          {filter === 'OPEN' ? 'Keine offenen Aufgaben für diesen Vertrag.' : 'Keine Aufgaben in dieser Ansicht.'}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {filtered.map(action => {
          const prio = PRIO_CONFIG[action.priority] || PRIO_CONFIG.NORMAL;
          const stat = STATUS_CONFIG_A[action.status] || {};
          return (
            <div key={action.id} style={{ background: '#1e293b', border: '1px solid #263244', borderRadius: 10, padding: '12px 14px', borderLeft: `3px solid ${prio.color}` }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: prio.color }}>{prio.label}</span>
                    <span style={{ fontSize: 10, color: '#475569' }}>·</span>
                    <span style={{ fontSize: 10, color: stat.color || '#64748b' }}>{stat.label || action.status}</span>
                    {action.source && action.source !== 'MANUAL' && (
                      <span style={{ fontSize: 10, color: '#64748b' }}>· {action.source}</span>
                    )}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9', lineHeight: 1.4 }}>{action.title}</div>
                  {action.description && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3 }}>{action.description}</div>}
                  {action.due_at && (
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Fällig: {formatDE(action.due_at.substring(0, 10))}</div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {action.status === 'OPEN' && (
                    <button onClick={() => handleComplete(action.id)} style={{ background: '#15803d', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 8px', cursor: 'pointer', fontSize: 11 }}>✓</button>
                  )}
                  {action.status === 'COMPLETED' && (
                    <button onClick={() => handleReopen(action.id)} style={{ background: '#334155', color: '#94a3b8', border: 'none', borderRadius: 5, padding: '4px 8px', cursor: 'pointer', fontSize: 11 }}>↩</button>
                  )}
                  {action.action_url && (
                    <button onClick={() => navigate(action.action_url)} style={{ background: '#1e3a5f', color: '#60a5fa', border: 'none', borderRadius: 5, padding: '4px 8px', cursor: 'pointer', fontSize: 11 }}>→</button>
                  )}
                  {action.auto_generated === 0 || action.auto_generated === false ? (
                    <button onClick={() => handleDismiss(action.id)} style={{ background: 'transparent', color: '#475569', border: '1px solid #334155', borderRadius: 5, padding: '4px 8px', cursor: 'pointer', fontSize: 11 }}>✕</button>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {actions.length > 0 && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <button onClick={() => navigate(`/actions?vertragId=${vertragId}`)} style={{
            background: 'transparent', color: '#4f46e5', border: '1px solid #4f46e5',
            borderRadius: 6, padding: '6px 16px', cursor: 'pointer', fontSize: 12,
          }}>
            Alle im Action Center →
          </button>
        </div>
      )}
    </div>
  );
}

function RadarTab({ vertragId, navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    radarApi.analyse({ contractId: vertragId })
      .then(setData)
      .catch(err => setError(err?.response?.data?.error || 'Radar-Analyse fehlgeschlagen.'))
      .finally(() => setLoading(false));
  }, [vertragId]);

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>📡 Analysiere Fristen & Risiken...</div>;
  if (error) return <div style={{ padding: 16, color: '#ef4444' }}>{error}</div>;
  if (!data?.events?.length) return (
    <div style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
      <div style={{ fontSize: 15, color: '#94a3b8' }}>Keine offenen Fristen oder Risiken für diesen Vertrag.</div>
    </div>
  );

  const { events } = data;
  return (
    <div style={{ padding: '16px 0' }}>
      {events.map(e => {
        const sev = RADAR_SEV_CFG[e.severity] || RADAR_SEV_CFG.info;
        const days = e.daysRemaining;
        return (
          <div key={e.id} style={{
            background: '#1e293b', border: `1px solid ${sev.color}44`,
            borderLeft: `4px solid ${sev.color}`, borderRadius: 10,
            padding: '12px 14px', marginBottom: 10,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 16 }}>{sev.icon}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9' }}>
                {e.title.replace(`${e.contractTitle} – `, '')}
              </span>
              <span style={{ fontSize: 10, color: sev.color, marginLeft: 'auto', fontWeight: 700 }}>
                {sev.label}
              </span>
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>{e.description}</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {days !== null && (
                <span style={{ fontSize: 12, fontWeight: 600, color: days < 0 ? '#ef4444' : days === 0 ? '#f97316' : '#64748b' }}>
                  {days < 0 ? `${Math.abs(days)} Tage überfällig` : days === 0 ? 'Heute' : `${days} Tage`}
                </span>
              )}
              {e.dueDate && <span style={{ fontSize: 11, color: '#475569' }}>{e.dueDate.slice(0,10).split('-').reverse().join('.')}</span>}
              <span style={{ fontSize: 10, color: '#64748b', fontWeight: 700, background: '#0f172a', borderRadius: 3, padding: '1px 5px' }}>
                {e.factType}
              </span>
            </div>
            {e.recommendedAction && (
              <button
                onClick={() => navigate(e.actionUrl || '/radar')}
                style={{ marginTop: 8, background: '#1e3a5f', border: '1px solid #2563eb',
                  color: '#93c5fd', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer' }}
              >
                {e.recommendedAction} →
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── EPIC 7: Optimierungs-Tab ─────────────────────────────────────────────────

const SIGNAL_TYPE_COLORS_D = {
  FAKT:      { bg: '#14532d', color: '#86efac' },
  INDIKATOR: { bg: '#1e1b4b', color: '#a5b4fc' },
  SCHÄTZUNG: { bg: '#431407', color: '#fed7aa' },
};

function OptimierungTab({ vertragId, navigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    optimierungApi.vertrag(vertragId)
      .then(setData)
      .catch(err => setError(err.response?.data?.error || 'Analyse fehlgeschlagen.'))
      .finally(() => setLoading(false));
  }, [vertragId]);

  if (loading) return <div style={{ padding: 20, color: '#64748b' }}>⏳ Analysiere...</div>;
  if (error) return <div style={{ padding: 20, color: '#f87171' }}>{error}</div>;
  if (!data) return null;

  const scoreColor = data.score >= 75 ? '#ef4444' : data.score >= 50 ? '#f59e0b' : data.score >= 25 ? '#3b82f6' : '#22c55e';
  const priorityBg = data.score >= 75 ? '#3b0000' : data.score >= 50 ? '#1c1400' : data.score >= 25 ? '#0c1a2e' : '#0f172a';

  return (
    <div style={{ padding: '0 0 20px' }}>
      {/* Score-Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 16, background: priorityBg, borderRadius: 10, marginBottom: 16, border: `1px solid ${scoreColor}44` }}>
        <div style={{ textAlign: 'center', minWidth: 64 }}>
          <div style={{ fontSize: 40, fontWeight: 900, color: scoreColor, lineHeight: 1 }}>{data.score}</div>
          <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>/ 100</div>
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9' }}>{data.priorityText}</div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
            {data.signals.length} Signal(e) erkannt · {data.confidence} Konfidenz
          </div>
        </div>
        <button onClick={() => navigate('/optimierung')} style={{
          marginLeft: 'auto', background: '#4f46e5', color: '#fff', border: 'none',
          borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12, flexShrink: 0,
        }}>
          Alle Verträge
        </button>
      </div>

      {/* Signale */}
      {data.signals.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            Erkannte Signale
          </div>
          {data.signals.map((s, i) => {
            const tc = SIGNAL_TYPE_COLORS_D[s.type] || SIGNAL_TYPE_COLORS_D['INDIKATOR'];
            return (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid #1e293b' }}>
                <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 5px', borderRadius: 3, flexShrink: 0, background: tc.bg, color: tc.color, marginTop: 1 }}>
                  {s.type}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>{s.title}</div>
                  {s.description && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, lineHeight: 1.5 }}>{s.description}</div>}
                </div>
                <span style={{ fontSize: 11, color: '#64748b', flexShrink: 0, marginTop: 1 }}>+{s.weight}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Einsparpotenzial */}
      {data.potentialSaving && (
        <div style={{ marginBottom: 16, background: '#1e293b', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
            Einsparpotenzial
          </div>
          <div style={{ fontSize: 13, color: '#e2e8f0', lineHeight: 1.6 }}>{data.potentialSaving.label}</div>
        </div>
      )}

      {/* Aktionen */}
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
          Empfohlene Aktionen
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.recommendedActions.map((a, i) => (
            <button key={i} onClick={() => navigate(a.url)} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: '#1e293b', border: '1px solid #334155', borderRadius: 8,
              padding: '10px 14px', cursor: 'pointer', color: '#e2e8f0', fontSize: 13, textAlign: 'left',
            }}>
              <span style={{ fontSize: 18 }}>{a.icon}</span>
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {data.signals.length === 0 && (
        <div style={{ textAlign: 'center', padding: 32, color: '#64748b' }}>
          ✅ Keine Auffälligkeiten in diesem Vertrag erkannt.
        </div>
      )}
    </div>
  );
}

// ─── EPIC 14: Kontoabbuchungen Tab ───────────────────────────────────────────
function KontoabbuchungenTab({ vertragId }) {
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    bankStatementsApi.contractTransactions(vertragId)
      .then(data => setTxs(data || []))
      .catch(() => setTxs([]))
      .finally(() => setLoading(false));
  }, [vertragId]);

  if (loading) return <div style={{ color: '#475569', textAlign: 'center', padding: 24 }}>Lädt…</div>;
  if (txs.length === 0) return (
    <div style={{ color: '#475569', textAlign: 'center', padding: 24 }}>
      Keine verknüpften Kontoabbuchungen gefunden.
      Lade einen Kontoauszug hoch und ordne diesen Vertrag zu.
    </div>
  );

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ color: '#64748b', fontSize: 12, marginBottom: 12 }}>
        {txs.length} verknüpfte Buchung{txs.length !== 1 ? 'en' : ''}
      </div>
      {txs.map(tx => (
        <div key={tx.id} style={{ background: '#1e293b', border: '1px solid #334155',
          borderRadius: 8, padding: '10px 14px', marginBottom: 8,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ color: '#f1f5f9', fontSize: 13 }}>{tx.counterparty || tx.booking_text || '—'}</div>
            {tx.purpose && (
              <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>{tx.purpose}</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexShrink: 0 }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 15, fontWeight: 700,
                color: tx.direction === 'CREDIT' ? '#22c55e' : '#f1f5f9' }}>
                {tx.direction === 'CREDIT' ? '+' : ''}
                {(tx.amount_cents / 100).toLocaleString('de-DE', { style: 'currency', currency: tx.currency || 'EUR' })}
              </div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{tx.booking_date || tx.value_date || ''}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const tabs = [
  { id: 'uebersicht', label: 'Übersicht' },
  { id: 'radar', label: '📡 Fristen & Risiken' },
  { id: 'aufgaben', label: '🎯 Aufgaben' },
  { id: 'finanzen', label: '💶 Finanzen' },
  { id: 'optimierung', label: '💡 Optimierung' },
  { id: 'fristen', label: 'Fristen & Timeline' },
  { id: 'simulator', label: 'Simulation' },
  { id: 'prognose', label: 'Verlängerung' },
  { id: 'dokumente', label: 'Dokumente' },
  { id: 'analyse', label: 'Dokumentenanalyse' },
  { id: 'ocr', label: 'OCR / KI' },
  { id: 'preishistorie', label: 'Preishistorie' },
  { id: 'erinnerungen', label: 'Erinnerungen' },
  { id: 'historie', label: 'Historie' },
  { id: 'kontoabbuchungen', label: '🏦 Kontoabbuchungen' },
];

export default function VertragDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [akte, setAkte] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('uebersicht');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [ocrResult, setOcrResult] = useState(null);
  const [showOcr, setShowOcr] = useState(false);
  const fileInputRef = useRef();
  const cameraInputRef = useRef();

  const load = () => {
    setLoading(true);
    vertraegeApi.akte(id)
      .then(res => setAkte(res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]);

  const vertrag = akte?.vertrag;
  const dokumente = akte?.dokumente || [];
  const berechnet = vertrag?.berechnet || {};
  const ki = akte?.ki || {};
  const statistik = akte?.statistik || {};
  const urgent = berechnet.tage_bis_kuendigung !== null && berechnet.tage_bis_kuendigung <= 30;

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true); setUploadProgress(0); setOcrResult(null);
    const formData = new FormData();
    formData.append('datei', file);
    try {
      const res = await dokumenteApi.upload(id, formData, e => {
        setUploadProgress(Math.round((e.loaded / e.total) * 100));
      });
      if (res.data.ocr_text) {
        setOcrResult(res.data.ocr_text);
        setShowOcr(true);
        setActiveTab('ocr');
      } else if (res.data.queued) {
        setOcrResult(null);
        setShowOcr(false);
        setActiveTab('ocr');
        setTimeout(load, 4000);
      }
      load();
    } catch (err) {
      alert('Upload fehlgeschlagen: ' + (err.response?.data?.error || err.message));
    } finally { setUploading(false); setUploadProgress(0); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Dokument löschen?')) return;
    await dokumenteApi.delete(docId);
    load();
  };

  const handleDeleteVertrag = async () => {
    if (!window.confirm(`Vertrag "${vertrag.titel}" wirklich löschen?`)) return;
    try {
      await vertraegeApi.delete(id);
      navigate('/vertraege', { replace: true });
    } catch (err) {
      alert(`Löschen fehlgeschlagen: ${err?.message || 'Unbekannter Fehler'}`);
    }
  };

  const handleStatusChange = async (newStatus) => {
    await vertraegeApi.update(id, { ...vertrag, status: newStatus });
    load();
  };

  if (loading) return <div style={{ padding: 20, color: '#64748b', textAlign: 'center' }}>⏳ Akte wird geladen...</div>;
  if (!vertrag) return <div style={{ padding: 20, color: '#f87171', textAlign: 'center' }}>Vertrag nicht gefunden</div>;

  return (
    <div style={{ padding: '18px', maxWidth: 1120, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={() => navigate(-1)} style={backBtn}>← Zurück</button>
        <div style={{ flex: 1 }} />
        <button onClick={() => navigate(`/ki/vertrag/${id}`)} style={secondaryBtn}>🔍 KI-Analyse</button>
        <button onClick={() => navigate(`/assistant?message=${encodeURIComponent(`Besprich den Vertrag ${vertrag.titel}`)}`)} style={secondaryBtn}>🤖 Mit KI besprechen</button>
        <button onClick={() => navigate(`/vergleich?leftContract=${id}`)} style={secondaryBtn}>⚖️ Vergleichen</button>
        <button onClick={() => navigate(`/vertraege/${id}/bearbeiten`)} style={secondaryBtn}>✏️ Bearbeiten</button>
      </div>

      <HeaderCard vertrag={vertrag} berechnet={berechnet} urgent={urgent} onStatusChange={handleStatusChange} />

      <div style={tabBar}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={tabButton(activeTab === tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'uebersicht' && (
        <OverviewTab vertrag={vertrag} berechnet={berechnet} statistik={statistik} />
      )}

      {activeTab === 'dokumente' && (
        <DocumentsTab
          dokumente={dokumente}
          uploading={uploading}
          uploadProgress={uploadProgress}
          fileInputRef={fileInputRef}
          cameraInputRef={cameraInputRef}
          onFileUpload={handleFileUpload}
          onDeleteDoc={handleDeleteDoc}
        />
      )}

      {activeTab === 'analyse' && (
        <DokumentAnalyseTab
          dokumente={dokumente}
          onAnalyseStarten={async (docId) => {
            try { await dokumenteApi.analysieren(docId); load(); }
            catch (e) { alert('Analyse konnte nicht gestartet werden: ' + (e.response?.data?.error || e.message)); }
          }}
        />
      )}

      {activeTab === 'ocr' && (
        <OcrKiTab
          ki={ki}
          dokumente={dokumente}
          ocrResult={ocrResult}
          showOcr={showOcr}
          setShowOcr={setShowOcr}
        />
      )}

      {activeTab === 'preishistorie' && (
        <PreishistorieSection
          historie={akte.preishistorie || []}
          vertragId={id}
          aktuelleKosten={vertrag.kosten}
          aktuellesIntervall={vertrag.zahlungsintervall}
          onChanged={load}
        />
      )}

      {activeTab === 'fristen' && <TimelineTab id={id} />}

      {activeTab === 'simulator' && <SimulatorTab id={id} />}

      {activeTab === 'prognose' && <PrognoseTab id={id} />}

      {activeTab === 'erinnerungen' && (
        <ErinnerungenTab erinnerungen={akte.erinnerungen || []} vertragId={id} />
      )}

      {activeTab === 'historie' && (
        <HistorieTabLegacy vertrag={vertrag} statistik={statistik} />
      )}

      {activeTab === 'radar' && (
        <RadarTab vertragId={id} navigate={navigate} />
      )}

      {activeTab === 'aufgaben' && (
        <AufgabenTab vertragId={id} navigate={navigate} />
      )}

      {activeTab === 'finanzen' && (
        <FinanzenTab vertragId={id} />
      )}

      {activeTab === 'optimierung' && (
        <OptimierungTab vertragId={id} navigate={navigate} />
      )}

      {activeTab === 'kontoabbuchungen' && (
        <KontoabbuchungenTab vertragId={id} />
      )}

      <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #334155' }}>
        <button onClick={handleDeleteVertrag} style={deleteBtn}>🗑️ Vertrag löschen</button>
      </div>
    </div>
  );
}

function HeaderCard({ vertrag, berechnet, urgent, onStatusChange }) {
  return (
    <div style={heroCard}>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 280px', minWidth: 0 }}>
          <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 8 }}>
            {vertrag.kategorie_icon || '📄'} {vertrag.kategorie_name || 'Ohne Kategorie'}
          </div>
          <h1 style={{ color: '#f8fafc', fontSize: 30, lineHeight: 1.15, margin: 0, overflowWrap: 'anywhere' }}>{vertrag.titel}</h1>
          <div style={{ color: '#cbd5e1', marginTop: 8, fontSize: 15 }}>{vertrag.anbieter || 'Kein Anbieter hinterlegt'}</div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={vertrag.status} onChange={e => onStatusChange(e.target.value)} style={statusSelect}>
            <option value="aktiv">✅ Aktiv</option>
            <option value="gekuendigt">❌ Gekündigt</option>
            <option value="abgelaufen">⏸ Abgelaufen</option>
          </select>
        </div>
      </div>

      <div style={metricGrid}>
        <Metric label="Monatskosten" value={formatEuro(berechnet.monatliche_kosten)} />
        <Metric label="Jahreskosten" value={formatEuro(berechnet.jaehrliche_kosten)} />
        <Metric label="Restlaufzeit" value={formatDays(berechnet.restlaufzeit_tage, 'Unbefristet')} />
        <Metric label="Kündigung" value={vertrag.naechste_kuendigung ? formatDatum(vertrag.naechste_kuendigung) : '–'} tone={urgent ? 'danger' : 'default'} />
        <Metric label="Auto-Verlängerung" value={berechnet.automatische_verlaengerung ? 'Ja' : 'Nein'} />
        <Metric label="Vertragsstatus" value={berechnet.vertragsstatus || vertrag.status || '–'} />
      </div>
    </div>
  );
}

function OverviewTab({ vertrag, berechnet, statistik }) {
  return (
    <div style={contentGrid}>
      <Card title="Vertragsdaten">
        <InfoGrid>
          <InfoItem label="Anbieter" value={vertrag.anbieter} />
          <InfoItem label="Kategorie" value={vertrag.kategorie_name} />
          <InfoItem label="Beginn" value={vertrag.vertragsbeginn ? formatDatum(vertrag.vertragsbeginn) : '–'} />
          <InfoItem label="Ende" value={vertrag.vertragsende ? formatDatum(vertrag.vertragsende) : 'Unbefristet'} />
          <InfoItem label="Kündigungsfrist" value={vertrag.kuendigungsfrist_monate ? `${vertrag.kuendigungsfrist_monate} Monat(e)` : '–'} />
          <InfoItem label="Tage bis Kündigung" value={formatDays(berechnet.tage_bis_kuendigung, '–')} highlight={berechnet.tage_bis_kuendigung !== null && berechnet.tage_bis_kuendigung <= 30} />
        </InfoGrid>
      </Card>

      <Card title="Akte">
        <InfoGrid>
          <InfoItem label="Dokumente" value={statistik.dokumentanzahl ?? 0} />
          <InfoItem label="OCR-Dokumente" value={statistik.ocr_dokumente ?? 0} />
          <InfoItem label="Letzte Änderung" value={statistik.letzte_aenderung ? formatDateTime(statistik.letzte_aenderung) : '–'} />
          <InfoItem label="Letzter Upload" value={statistik.letzter_upload ? formatDateTime(statistik.letzter_upload) : '–'} />
        </InfoGrid>
      </Card>

      <Card title="Notizen" wide>
        {vertrag.notizen ? (
          <p style={bodyText}>{vertrag.notizen}</p>
        ) : (
          <EmptyState text="Keine Notizen hinterlegt." />
        )}
      </Card>
    </div>
  );
}

function DocumentsTab({ dokumente, uploading, uploadProgress, fileInputRef, cameraInputRef, onFileUpload, onDeleteDoc }) {
  return (
    <Card title={`Dokumente (${dokumente.length})`} wide>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => cameraInputRef.current?.click()} disabled={uploading} style={uploadBtn('#6366f1')}>
          📷 Foto aufnehmen
        </button>
        <button onClick={() => fileInputRef.current?.click()} disabled={uploading} style={uploadBtn('#0891b2')}>
          📎 Datei wählen
        </button>
      </div>

      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
        style={{ display: 'none' }} onChange={e => onFileUpload(e.target.files[0])} />
      <input ref={fileInputRef} type="file" accept="image/*,application/pdf"
        style={{ display: 'none' }} onChange={e => onFileUpload(e.target.files[0])} />

      {uploading && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 6 }}>⏳ Hochladen & OCR läuft... {uploadProgress}%</div>
          <div style={{ background: '#334155', borderRadius: 4, height: 6 }}>
            <div style={{ background: '#38bdf8', height: 6, borderRadius: 4, width: `${uploadProgress}%`, transition: 'width 0.3s' }} />
          </div>
        </div>
      )}

      {dokumente.length === 0 ? (
        <EmptyState text="Noch keine Dokumente in dieser Vertragsakte." />
      ) : (
        <div style={documentGrid}>
          {dokumente.map(doc => (
            <DokumentCard key={doc.id} doc={doc} onDelete={() => onDeleteDoc(doc.id)} />
          ))}
        </div>
      )}
    </Card>
  );
}

function OcrKiTab({ ki, dokumente, ocrResult, showOcr, setShowOcr }) {
  const ocrDocs = dokumente.filter(doc => doc.ocr_vorhanden || doc.ocr_text);
  return (
    <div style={contentGrid}>
      <Card title="KI-Auswertung" wide>
        {ki?.verfuegbar ? (
          <>
            <p style={bodyText}>{ki.zusammenfassung}</p>
            {ki.empfehlungen?.length > 0 && ki.empfehlungen.map((empfehlung, index) => (
              <div key={index} style={pill}>{empfehlung}</div>
            ))}
          </>
        ) : (
          <EmptyState text="Für diesen Vertrag liegt noch keine KI-Auswertung vor." />
        )}
      </Card>

      {showOcr && ocrResult && (
        <Card title="Gerade erkannter OCR-Text" wide>
          <button onClick={() => setShowOcr(false)} style={{ ...secondaryBtn, marginBottom: 10 }}>Ausblenden</button>
          <p style={{ ...bodyText, maxHeight: 220, overflow: 'auto' }}>{ocrResult}</p>
          <KiAnalyse ocrText={ocrResult} onUebernehmen={() => {
            alert('KI-Daten erkannt. Bitte bearbeite den Vertrag, um die Daten zu übernehmen.');
          }} />
        </Card>
      )}

      <Card title={`OCR-Dokumente (${ocrDocs.length})`} wide>
        {ocrDocs.length === 0 ? (
          <EmptyState text="Noch kein OCR-Text in Dokumenten vorhanden." />
        ) : (
          ocrDocs.map(doc => (
            <div key={doc.id} style={ocrRow}>
              <strong style={{ color: '#f8fafc' }}>{doc.originaldateiname || doc.dateiname}</strong>
              <span style={{ color: '#94a3b8', fontSize: 12 }}>OCR vorhanden · {formatBytes(doc.groesse)}</span>
            </div>
          ))
        )}
      </Card>
    </div>
  );
}

export function PreishistorieSection({ historie, vertragId, aktuelleKosten, aktuellesIntervall, onChanged }) {
  const [showForm, setShowForm] = React.useState(false);
  const [neuerPreis, setNeuerPreis] = React.useState('');
  const [notiz, setNotiz] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const handleSave = async () => {
    if (!neuerPreis) return;
    setSaving(true);
    try {
      await preishistorieApi.add(vertragId, {
        kosten_alt: aktuelleKosten,
        kosten_neu: parseFloat(neuerPreis),
        zahlungsintervall: aktuellesIntervall,
        notiz,
      });
      setShowForm(false);
      setNeuerPreis('');
      setNotiz('');
      onChanged?.();
    } finally { setSaving(false); }
  };

  return (
    <Card title="Preishistorie" wide>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <p style={{ color: '#94a3b8', margin: 0, fontSize: 13 }}>Preisänderungen als Timeline der Vertragsakte.</p>
        <button onClick={() => setShowForm(!showForm)} style={secondaryBtn}>
          {showForm ? '✕ Abbrechen' : '+ Preisänderung'}
        </button>
      </div>

      {showForm && (
        <div style={formBox}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 10 }}>
            <InputInfo label="Alter Preis" value={aktuelleKosten ? formatEuro(aktuelleKosten) : '–'} />
            <div>
              <label style={labelStyle}>Neuer Preis (€)</label>
              <input type="number" value={neuerPreis} onChange={e => setNeuerPreis(e.target.value)}
                placeholder="0.00" step="0.01" min="0" style={inputStyle} />
            </div>
          </div>
          <input value={notiz} onChange={e => setNotiz(e.target.value)}
            placeholder="Grund (z.B. Jahreserhöhung 2025)" style={{ ...inputStyle, marginBottom: 10 }} />
          <button onClick={handleSave} disabled={saving} style={primaryBtn}>
            {saving ? '⏳ Speichern...' : '💾 Speichern'}
          </button>
        </div>
      )}

      {historie.length === 0 ? (
        <EmptyState text="Noch keine Preisänderungen erfasst." />
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {historie.map(h => <TimelineItem key={h.id} item={h} />)}
        </div>
      )}
    </Card>
  );
}

function ErinnerungenTab({ erinnerungen, vertragId }) {
  const [notifications, setNotifications] = React.useState([]);
  const [createTitle, setCreateTitle] = React.useState('');
  const [createDate, setCreateDate] = React.useState('');
  const [creating, setCreating] = React.useState(false);

  React.useEffect(() => {
    if (vertragId) notificationsApi.list({ contractId: vertragId, limit: 20 }).then(setNotifications).catch(() => {});
  }, [vertragId]);

  async function handleCreate(e) {
    e.preventDefault(); if (!createTitle || !createDate) return;
    setCreating(true);
    try { await notificationsApi.createManual({ title: createTitle, scheduledFor: createDate, contractId: Number(vertragId) }); setCreateTitle(''); setCreateDate(''); notificationsApi.list({ contractId: vertragId, limit: 20 }).then(setNotifications).catch(() => {}); }
    catch (ex) { alert(ex.message); }
    setCreating(false);
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Card title={`🔔 Benachrichtigungen (${notifications.length})`} wide>
        <form onSubmit={handleCreate} style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
          <input required placeholder="Erinnerungstitel" value={createTitle} onChange={e => setCreateTitle(e.target.value)}
            style={{ flex: 1, minWidth: 150, background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#f1f5f9', padding: '6px 10px', fontSize: 13 }} />
          <input required type="date" min={new Date().toISOString().slice(0,10)} value={createDate} onChange={e => setCreateDate(e.target.value)}
            style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#f1f5f9', padding: '6px 10px', fontSize: 13 }} />
          <button type="submit" disabled={creating} style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13 }}>+ Erinnerung</button>
        </form>
        {notifications.length === 0 ? (
          <EmptyState text="Keine Benachrichtigungen für diesen Vertrag." />
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {notifications.map(n => (
              <div key={n.id} style={{ ...timelineCard, borderLeftColor: n.severity === 'CRITICAL' ? '#ef4444' : n.severity === 'HIGH' ? '#f97316' : '#334155', borderLeftWidth: 3 }}>
                <div style={{ color: '#f8fafc', fontWeight: 700, fontSize: 13 }}>{n.title}</div>
                <div style={{ color: '#94a3b8', fontSize: 12 }}>{n.scheduled_for} · {n.severity} · {n.status}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
      {erinnerungen.length > 0 && (
        <Card title={`E-Mail Erinnerungen (${erinnerungen.length})`} wide>
          <div style={{ display: 'grid', gap: 10 }}>
            {erinnerungen.map(erinnerung => (
              <div key={erinnerung.id} style={timelineCard}>
                <div style={{ color: '#f8fafc', fontWeight: 700 }}>{erinnerung.typ || 'Erinnerung'}</div>
                <div style={{ color: '#94a3b8', fontSize: 13 }}>{erinnerung.gesendet_am ? formatDateTime(erinnerung.gesendet_am) : 'Kein Versanddatum'}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function HistorieTabLegacy({ vertrag, statistik }) {
  return (
    <div style={contentGrid}>
      <Card title="Änderungsprotokoll">
        <HistorieTab id={vertrag.id} />
      </Card>
      <Card title="Metadaten">
        <InfoGrid>
          <InfoItem label="Erstellt" value={vertrag.erstellt_am ? formatDateTime(vertrag.erstellt_am) : '–'} />
          <InfoItem label="Aktualisiert" value={vertrag.aktualisiert_am ? formatDateTime(vertrag.aktualisiert_am) : '–'} />
          <InfoItem label="Letzter Upload" value={statistik.letzter_upload ? formatDateTime(statistik.letzter_upload) : '–'} />
        </InfoGrid>
      </Card>
    </div>
  );
}

function DokumentCard({ doc, onDelete }) {
  const navigate = useNavigate();
  const isImage = doc.dateityp?.startsWith('image/');
  const url = `${process.env.REACT_APP_API_URL?.replace('/api', '') || ''}/uploads/${doc.dateiname}`;
  const vstatus = STATUS_VERARB[doc.verarbeitungsstatus];
  return (
    <div style={documentCard}>
      <div style={{ display: 'flex', gap: 12, minWidth: 0 }}>
        <div style={documentIcon}>{isImage ? '🖼️' : '📄'}</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ color: '#f8fafc', fontSize: 14, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {doc.originaldateiname || doc.dateiname}
          </div>
          <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>
            {formatBytes(doc.groesse)} · {doc.hochgeladen_am ? formatDateTime(doc.hochgeladen_am) : '–'}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ color: doc.ocr_vorhanden || doc.ocr_text ? '#38bdf8' : '#64748b', fontSize: 12 }}>
              {doc.ocr_vorhanden || doc.ocr_text ? '🔍 OCR' : 'kein OCR'}
            </span>
            {vstatus && (
              <span style={{ color: vstatus.color, fontSize: 11, background: vstatus.color + '22',
                border: `1px solid ${vstatus.color}44`, borderRadius: 4, padding: '1px 6px' }}>
                {vstatus.label}
              </span>
            )}
            {doc.klassifikation && (
              <span style={{ color: '#a5b4fc', fontSize: 11 }}>{doc.klassifikation}</span>
            )}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <a href={url} target="_blank" rel="noopener noreferrer" style={docAction}>👁️ Öffnen</a>
        <button onClick={() => navigate(`/dokumente/${doc.id}`)} style={docAction}>🔬 Analyse</button>
        <button onClick={onDelete} style={{ ...docAction, color: '#f87171' }}>🗑️ Löschen</button>
      </div>
    </div>
  );
}

function TimelineItem({ item }) {
  const diff = (Number(item.kosten_neu) || 0) - (Number(item.kosten_alt) || 0);
  const color = diff > 0 ? '#f87171' : diff < 0 ? '#34d399' : '#94a3b8';
  return (
    <div style={timelineCard}>
      <div style={timelineDot} />
      <div style={{ flex: 1 }}>
        <div style={{ color: '#f8fafc', fontWeight: 700 }}>
          {formatEuro(item.kosten_alt)} → {formatEuro(item.kosten_neu)}
          <span style={{ color, marginLeft: 8, fontSize: 13 }}>{diff > 0 ? '+' : ''}{formatEuro(diff)}</span>
        </div>
        <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>
          {item.geaendert_am ? formatDateTime(item.geaendert_am) : '–'} · {item.zahlungsintervall || 'Intervall unbekannt'}
        </div>
        {item.notiz && <div style={{ color: '#cbd5e1', fontSize: 13, marginTop: 8 }}>{item.notiz}</div>}
      </div>
    </div>
  );
}

function Card({ title, children, wide }) {
  return (
    <section style={{ ...cardStyle, gridColumn: wide ? '1 / -1' : undefined }}>
      <h2 style={cardTitle}>{title}</h2>
      {children}
    </section>
  );
}

function Metric({ label, value, tone = 'default' }) {
  return (
    <div style={metricCard}>
      <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 6 }}>{label}</div>
      <div style={{ color: tone === 'danger' ? '#f87171' : '#f8fafc', fontSize: 18, fontWeight: 800 }}>{value || '–'}</div>
    </div>
  );
}

function InfoGrid({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>{children}</div>;
}

function InfoItem({ label, value, highlight }) {
  return (
    <div style={infoItem}>
      <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 5 }}>{label}</div>
      <div style={{ color: highlight ? '#f87171' : '#f8fafc', fontWeight: 700, overflowWrap: 'anywhere' }}>{value || '–'}</div>
    </div>
  );
}

function InputInfo({ label, value }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ ...inputStyle, color: '#94a3b8' }}>{value}</div>
    </div>
  );
}

function EmptyState({ text }) {
  return <div style={{ color: '#94a3b8', background: '#0f172a', border: '1px dashed #334155', borderRadius: 8, padding: 16, fontSize: 14 }}>{text}</div>;
}

const backBtn = { background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', fontSize: 14, padding: '4px 0' };
const secondaryBtn = { background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, textDecoration: 'none' };
const primaryBtn = { background: '#818cf8', color: 'white', border: 'none', padding: '9px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700, minHeight: 36 };
const deleteBtn = { background: '#ef444422', color: '#f87171', border: '1px solid #ef444444', padding: '11px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 13, width: '100%' };
const statusSelect = { background: '#0f172a', color: '#f1f5f9', border: '1px solid #334155', padding: '8px 10px', borderRadius: 8, fontSize: 13 };
const uploadBtn = (color) => ({ background: `${color}22`, color, border: `1px solid ${color}55`, padding: '10px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, flex: '1 1 180px' });
const heroCard = { background: 'linear-gradient(135deg, #172033 0%, #1e293b 65%, #10253d 100%)', border: '1px solid #334155', borderRadius: 12, padding: 22, marginBottom: 16, boxShadow: '0 18px 45px rgba(0,0,0,0.22)' };
const metricGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginTop: 22 };
const metricCard = { background: 'rgba(15, 23, 42, 0.78)', border: '1px solid #334155', borderRadius: 10, padding: 14, minHeight: 76 };
const tabBar = { display: 'flex', gap: 8, overflowX: 'auto', padding: '4px 0 14px', marginBottom: 4 };
const tabButton = (active) => ({ flex: '0 0 auto', background: active ? '#818cf8' : '#1e293b', color: active ? 'white' : '#cbd5e1', border: active ? '1px solid #818cf8' : '1px solid #334155', padding: '9px 13px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: active ? 700 : 500, minHeight: 36 });
const contentGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 };
const cardStyle = { background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 16, minWidth: 0 };
const cardTitle = { color: '#f8fafc', fontSize: 16, fontWeight: 800, margin: '0 0 14px' };
const infoItem = { background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 12, minWidth: 0 };
const bodyText = { color: '#cbd5e1', fontSize: 14, whiteSpace: 'pre-wrap', lineHeight: 1.6, margin: 0 };
const documentGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 };
const documentCard = { background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 14, minWidth: 0 };
const documentIcon = { width: 38, height: 38, borderRadius: 8, background: '#1e293b', display: 'grid', placeItems: 'center', fontSize: 20, flex: '0 0 auto' };
const docAction = { background: '#1e293b', color: '#cbd5e1', border: '1px solid #334155', padding: '7px 10px', borderRadius: 7, cursor: 'pointer', fontSize: 12, textDecoration: 'none' };
const ocrRow = { display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 12, marginBottom: 10 };
const pill = { display: 'inline-flex', background: '#0f172a', border: '1px solid #334155', color: '#cbd5e1', borderRadius: 999, padding: '6px 10px', margin: '6px 6px 0 0', fontSize: 12 };
const formBox = { background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 14, marginBottom: 14 };
const inputStyle = { width: '100%', boxSizing: 'border-box', background: '#1e293b', border: '1px solid #334155', color: '#f1f5f9', padding: '9px 10px', borderRadius: 7, fontSize: 13, outline: 'none' };
const labelStyle = { color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 5 };
const timelineCard = { position: 'relative', display: 'flex', gap: 12, background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 14 };
const timelineDot = { width: 10, height: 10, borderRadius: 999, background: '#38bdf8', marginTop: 5, flex: '0 0 auto' };

function formatEuro(v) { return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v) || 0); }
function formatDatum(d) { return format(parseISO(String(d)), 'dd.MM.yyyy', { locale: de }); }
function formatDateTime(d) { return format(parseISO(String(d).replace(' ', 'T')), 'dd.MM.yyyy HH:mm', { locale: de }); }
function formatDays(days, fallback) {
  if (days === null || days === undefined) return fallback;
  if (days < 0) return `${Math.abs(days)} Tage überfällig`;
  if (days === 0) return 'Heute';
  return `${days} Tage`;
}
function formatBytes(b) { if (!b) return '0 B'; if (b < 1024) return `${b} B`; if (b < 1048576) return `${(b/1024).toFixed(0)} KB`; return `${(b/1048576).toFixed(1)} MB`; }
